import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import { asJxgPoint, type ElementCreatorContext } from '../core/board.types';
import { parseThemeColor, resolveColor } from '../core/color.utils';
import { deepMerge } from '../core/deepMerge';
import { getBisectorPoint, isElement } from '../core/element.utils';
import { attachReconfigure, bindLabel, createAngleLabelContext } from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import { createPoint } from '../point/point.create';
import type { PointCoordsInput } from '../point/point.model';
import type { AngleStyle, ElementTheme } from '../theme/theme.model';
import type { PointInput } from '../line/line.model';
import type { AngleArgs, AngleResult } from './angle.model';

/**
 * Check if input is coordinates.
 */
function isCoords(input: PointInput): input is PointCoordsInput {
  return Array.isArray(input) && input.length === 2;
}

/**
 * Build JSXGraph angle attributes from style configuration.
 */
function buildAngleAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: ElementTheme['angle'],
  styleOverrides?: AngleStyle,
): Record<string, unknown> {
  const style = deepMerge(baseStyle, styleOverrides);

  const fillColor = resolveColor(themeService, style.fillColor);
  const strokeColor = resolveColor(themeService, style.strokeColor);

  return {
    fillColor,
    fillOpacity: style.fillOpacity,
    strokeColor,
    radius: style.radius,
    type: style.type,
    visible: styleOverrides?.visible ?? true,
    withLabel: false,
    orthoType: style.type === 'square' ? 'square' : 'sector',
  };
}

/**
 * Resolve a point input to a JSXGraph element.
 */
function resolvePoint(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  input: PointInput,
  name: string,
  createIfCoords: boolean,
): GeometryElement | null {
  const { board } = ctx;

  if (isElement(input)) {
    return input;
  }

  if (typeof input === 'string') {
    const found = board.elementsByName[input];
    if (!found) {
      throw new Error(`Point "${input}" not found on board`);
    }
    return found as GeometryElement;
  }

  if (isCoords(input) && createIfCoords) {
    const result = createPoint(ctx, themeService, theme, {
      coords: input,
      name,
      style: { visible: false }, // Hide angle construction points by default
    });
    return result.point;
  }

  if (isCoords(input)) {
    // Create invisible point just for angle construction
    const point = board.create('point', input, {
      visible: false,
      withLabel: false,
    });
    return point as GeometryElement;
  }

  return null;
}

/**
 * Create an angle element with optional label.
 * Label is now configured via style.label.
 *
 * The angle is defined by three points:
 * - a: First point (one ray)
 * - vertex: The vertex of the angle
 * - b: Third point (other ray)
 *
 * The angle is measured from ray (vertex → a) to ray (vertex → b) counter-clockwise.
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Angle creation arguments
 * @returns Angle result with element and optional label
 */
export function createAngle(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: AngleArgs,
): AngleResult {
  const { board } = ctx;
  const {
    a: inputA,
    vertex: inputVertex,
    b: inputB,
    name = '',
    style,
    createPoints = false,
    pointNames = ['A', 'V', 'B'],
  } = args;

  // Resolve or create points
  const pointA = resolvePoint(ctx, themeService, theme, inputA, pointNames[0], createPoints);
  const vertexPoint = resolvePoint(ctx, themeService, theme, inputVertex, pointNames[1], createPoints);
  const pointB = resolvePoint(ctx, themeService, theme, inputB, pointNames[2], createPoints);

  if (!pointA || !vertexPoint || !pointB) {
    throw new Error('Failed to resolve angle points');
  }

  // Build angle attributes
  const angleAttrs = buildAngleAttributes(themeService, theme.angle, style);

  // Create the angle (JSXGraph expects points in order: a, vertex, b)
  const angle = board.create('angle', [pointA, vertexPoint, pointB], angleAttrs) as GeometryElement;

  const result: AngleResult = { angle } as AngleResult;

  // Include points in result if they were created or are useful for reference
  if (!isElement(inputA)) {
    result.a = pointA;
  }
  if (!isElement(inputVertex)) {
    result.vertex = vertexPoint;
  }
  if (!isElement(inputB)) {
    result.b = pointB;
  }

  // Create label if configured - now from style.label
  const labelColor = parseThemeColor(style?.fillColor ?? 'neutral').name;

  // Resolve placement from AngleLabelConfig.placement (preferred) or deprecated labelOutside
  const labelObj = typeof style?.label === 'object' ? style.label : undefined;
  const outside = labelObj?.placement === 'outside' || style?.labelOutside === true;

  const labelConfig = resolveLabelConfig(style?.label, {
    color: labelColor,
    position: 'top',
    spacing: outside ? 'normal' : 'compact',
    contentVariant: 'name+deg', // Default to showing angle degrees
    placement: outside ? 'outside' : 'inside',
    precision: ctx.defaultPrecision,
  });

  // Always create label infrastructure — visibility is controlled via config
  const typedA = asJxgPoint(pointA);
  const typedV = asJxgPoint(vertexPoint);
  const typedB = asJxgPoint(pointB);

  let cachedBisector: [number, number] = [0, 0];
  let cachedBisectorAngle = 0;

  const labelPoint = board.create(
    'point',
    [
      () => {
        // Anchor the label at the vertex. Slope-driven positioning picks the
        // correct anchor alignment and the pixel offset pushes the label along
        // the bisector direction (inward or outward).
        const interiorPt = getBisectorPoint(typedA, typedV, typedB, 1);
        const interiorAngle = Math.atan2(interiorPt[1] - typedV.Y(), interiorPt[0] - typedV.X());
        cachedBisectorAngle = outside ? interiorAngle + Math.PI : interiorAngle;
        cachedBisector = [typedV.X(), typedV.Y()];
        return cachedBisector[0];
      },
      () => cachedBisector[1],
    ],
    { visible: false },
  ) as GeometryElement;
  result.helpers = [labelPoint];

  const boundLabel = bindLabel({
    ctx,
    themeService,
    element: labelPoint,
    config: labelConfig,
    contextProvider: createAngleLabelContext(name, angle),
    // Rotate by -π/2 so pickPositionFromSlope (which picks perpendicular to slope)
    // places the label in the bisector direction, away from polygon edges.
    slopeProvider: () => cachedBisectorAngle - Math.PI / 2,
  });
  result.label = boundLabel;

  // Attach reconfigure to the angle element so PATCH_DISPLAY can update
  // labels via the same target that controls angle style (e.g., 't1.angle.alpha').
  attachReconfigure(angle, boundLabel);

  return result;
}
